import type { SketchGroup } from '../../../mesh/sketch';
import { SketchProfileBindingPayloadBuilder } from '../../runtime/SketchProfileBindingPayloadBuilder';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/revolve 层；
 * 2. 负责把旋转凸台会话输入转换为持久化 binding；
 * 3. 该工厂不参与命令参数计算，只维护特征输入引用协议。
 *
 * 文件职责：
 * 1. 构建草图轮廓 sketch-profile binding；
 * 2. 构建旋转轴 sketch-sub-entity binding；
 * 3. 保证旋转创建、编辑与重建共享同一份输入绑定表达。
 */

/**
 * 旋转凸台轮廓 binding 输入。
 */
export interface BossRevolveContourBindingInput {
    /** 轮廓源对象 ID。 */
    contourSourceObjectId?: string;
    /** 轮廓键。 */
    contourKey?: string;
    /** 区域几何。 */
    regionGeometry: ProfileRegionGeometry;
    /** 相交闭合区域身份。 */
    regionIdentity?: PersistedRegionIdentity;
}

/**
 * 旋转凸台 binding 工厂。
 */
export class BossRevolveFeatureBindingFactory {
    /**
     * 构建序列化 binding 集合。
     *
     * @param input 构建输入。
     * @returns 序列化 binding 集合。
     */
    public static buildSerializedBindings(input: {
        sketchId?: string | null;
        sketchGroup?: SketchGroup | null;
        contours: BossRevolveContourBindingInput[];
        axisReference?: RevolveAxisReference;
    }): SerializedFeatureReferenceBinding[] {
        const bindings: SerializedFeatureReferenceBinding[] = [];
        if (input.sketchId && input.contours.length > 0) {
            bindings.push(SketchProfileBindingPayloadBuilder.build({
                sketchId: input.sketchId,
                sketchGroup: input.sketchGroup ?? null,
                contours: input.contours.map((contour) => ({
                    contourSourceObjectId: contour.contourSourceObjectId,
                    contourKey: contour.contourKey,
                    regionGeometry: contour.regionGeometry,
                    regionIdentity: contour.regionIdentity,
                })),
            }));
        }

        if (input.axisReference?.kind === 'sketch_edge') {
            bindings.push({
                kind: 'sketch-sub-entity',
                sketchId: input.axisReference.sourceRef.ownerSketchId,
                sourceRef: {
                    ...input.axisReference.sourceRef,
                },
            });
        }

        return bindings;
    }
}
